type Nullish = undefined | null;

export interface Page<T> {
  items: T[];
}

export type User = {
  id: string;
  name: string;
  type: 'user';
  /** A list of IDs of groups the user is member in. */
  groups?: string[] | Nullish;
  avatar?: string | Nullish;
  phone?: string | Nullish;
  email?: string | Nullish;
  extid?: string | Nullish;
  external_id?: string | Nullish;
  custom?: {
    // will be filled with platform-specifics that don't match the above
    [name: string]: unknown;
  };
};

export type Group = {
  id: string;
  name: string;
  type: 'group';
  userCount?: number | Nullish;
  avatar?: string | Nullish;
  custom?: {
    // will be filled with platform-specifics that don't match the above
    [name: string]: unknown;
  };
};

export type Identity = User | Group;
export type IdentityTypes = 'user' | 'group';

/**
 * Who a notification is delivered to, when the plugin cannot enumerate the
 * audience itself.
 *
 * `all` means every user of the instance's tenant. The roster is resolved by
 * the platform mapper, so the plugin neither has to fetch nor to ship
 * thousands of ids. Not every platform can serve it — mappers that cannot log
 * and skip delivery (see `PlatformMapper.resolveBroadcastRecipients`).
 */
export type NotificationAudience = 'all';

export interface Notification {
  /**
   * Explicit recipient ids. May be empty when {@link audience} is set, which
   * is the only case in which an empty list is not an error.
   */
  recipients: string[];
  /** Resolve the recipients on the platform instead of listing them. */
  audience?: NotificationAudience;
  /**
   * Narrow a broadcast ({@link audience} `all`) to users of these locations.
   *
   * Only honoured by platforms that know a location per user (currently Zoom);
   * everywhere else it is ignored. An empty or missing list delivers to
   * nobody, the single entry `all` delivers to everybody — including users
   * without a location on their profile.
   *
   * Has no effect on explicit {@link recipients}: those were picked
   * deliberately and are always delivered to.
   */
  locations?: string[];
  /**
   * Widen a broadcast ({@link audience} `all`) to the members of these groups,
   * identified by their platform group id (see the `group` api).
   *
   * Evaluated **next to** {@link locations}, not on top of it: a user is
   * addressed when they match the locations *or* sit in one of the groups. A
   * notification that names neither therefore still delivers to nobody, which
   * is what it did before groups existed.
   *
   * Only honoured by platforms that know a user's group membership (currently
   * Zoom); everywhere else it is ignored. Has no effect on explicit
   * {@link recipients}.
   */
  groups?: string[];
  content: NotificationContent;
  link?: string;
  image?: NotificationImage;
}

export interface NotificationImage {
  en: {
    href: string;
  };
  de: {
    href: string;
  };
}

export interface NotificationContent {
  en: {
    title: string;
  };
  de: {
    title: string;
  };
}

export const StoreKeyTypes = ['tenant', 'editor', 'user'] as const;
export const SetStoreKeyTypes = ['tenant', 'editor', 'user'] as const;
export type StoreKeyType = (typeof StoreKeyTypes)[number];
export type SetStoreKeyType = (typeof SetStoreKeyTypes)[number];
export type DeleteStoreKeyType = SetStoreKeyType;
export type AdminStoreKeyType = Exclude<(typeof StoreKeyTypes)[number], 'user'>;
export const StoreKeyDefault = 'default';
export const StoreSecretKeyPrefix = 's:';
export const StoreKeyOverrides = 'instanceOverrides';
export interface StoreKey {
  type: StoreKeyType;
  key: string;
  isSecret?: boolean;
}
export interface SetStoreKey extends StoreKey {
  type: SetStoreKeyType;
}
export interface StoreEntry<T> extends StoreKey {
  value: T;
}
export interface SetStoreEntry<T> extends SetStoreKey {
  value: T;
}
export interface DeleteStoreKey extends StoreKey {
  type: DeleteStoreKeyType;
}
